import { z } from 'zod/v4';

/**
 * The entity-screening "involved orders" contract:
 * `GET /api/entity-scan/purchase-orders/company/:companyId`, the caller's
 * purchase orders a screened company takes part in.
 */

/** What the involved-orders table sorts by. */
export const INVOLVED_ORDERS_SORT_FIELDS = [
  'orderNumber',
  'agreedDeliveryDate',
  'tags',
] as const;
export type InvolvedOrdersSortField =
  (typeof INVOLVED_ORDERS_SORT_FIELDS)[number];

export const InvolvedOrdersParamsSchema = z.object({
  /** The screened company. */
  companyId: z.string().min(1),
});
export type InvolvedOrdersParams = z.infer<typeof InvolvedOrdersParamsSchema>;

export const InvolvedOrdersQuerySchema = z.object({
  /** 1-based. */
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(15),
  /**
   * `<field>[:asc|desc]`; newest order number first without it. The parsed
   * `{ field, direction }` is accepted too, so parsing twice (the route's
   * validation, then the handler) is harmless.
   */
  sort: z
    .union([
      z.object({
        field: z.enum(INVOLVED_ORDERS_SORT_FIELDS),
        direction: z.enum(['asc', 'desc']),
      }),
      z
        .string()
        .regex(
          new RegExp(
            `^(${INVOLVED_ORDERS_SORT_FIELDS.join('|')})(:(asc|desc))?$`,
            'i',
          ),
        )
        .transform(value => {
          const [field, direction = 'desc'] = value.split(':');
          return {
            field: field as InvolvedOrdersSortField,
            direction: direction.toLowerCase() as 'asc' | 'desc',
          };
        }),
    ])
    .default({ field: 'orderNumber', direction: 'desc' }),
  /** Part of the order number, any case. */
  orderNumber: z.string().optional(),
  /** Comma-separated tags; an order carrying any of them matches. */
  tags: z.string().optional(),
  /** Orders with a line of this product code; only those lines are listed. */
  productCode: z.string().optional(),
});
export type InvolvedOrdersQuery = z.infer<typeof InvolvedOrdersQuerySchema>;

export const InvolvedOrderProductSchema = z.object({
  code: z.string(),
  /** The line's quantity, rounded to a whole number. */
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
});

/** One of the caller's purchase orders the screened company takes part in. */
export const InvolvedOrderSchema = z.object({
  purchaseOrderId: z.string(),
  orderNumber: z.string().nullable(),
  /** ISO date-time. */
  agreedDeliveryDate: z.string().nullable(),
  /** The order's tags, comma-separated. */
  purchaseOrderTags: z.string().nullable(),
  /** Its lines' products. */
  products: z.array(InvolvedOrderProductSchema),
});
export type InvolvedOrder = z.infer<typeof InvolvedOrderSchema>;
